import { createFileRoute, Link } from "@tanstack/react-router";
import { staffApi, staffKeys, useStaffQuery } from "@/lib/staff";
import { StaffOnly, StateBadge } from "@/components/staff/common";
import { PartnerOverview } from "@/components/staff/PartnerOverview";
import { SourcesTab } from "@/components/staff/SourcesTab";
import { StagesTab } from "@/components/staff/StagesTab";
import { CredentialsTab } from "@/components/staff/CredentialsTab";
import { ActivityTab } from "@/components/staff/ActivityTab";
import { AuditTab, UsageTab } from "@/components/staff/HistoryTabs";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Page, PageHeader, PageScroll } from "@/components/layout/Page";

export const Route = createFileRoute("/staff/extraction/$partnerId")({
  component: () => (
    <StaffOnly>
      <PartnerPage />
    </StaffOnly>
  ),
});

function PartnerPage() {
  const { partnerId } = Route.useParams();
  const platform = partnerId === "platform";
  const partner = useStaffQuery(staffKeys.partner(partnerId), () => staffApi.partner(partnerId), {
    enabled: !platform,
  });

  const back = (
    <Link to="/staff/extraction" className="text-sm text-chrome/70 hover:text-chrome">
      ← Document pipeline
    </Link>
  );

  if (platform) {
    return (
      <Page>
        <PageHeader
          eyebrow={back}
          title="Platform defaults"
          description="What every partner inherits until it is given its own: the default model, prompt and mode per stage, and platform keys a partner may be pointed at explicitly. Nothing here is used silently on a partner's behalf."
        />
        <PageScroll>
          <Tabs defaultValue="stages">
            <TabsList>
              <TabsTrigger value="stages">Models &amp; prompts</TabsTrigger>
              <TabsTrigger value="keys">Platform keys</TabsTrigger>
              <TabsTrigger value="audit">Changes</TabsTrigger>
            </TabsList>
            <TabsContent value="stages" className="px-0">
              <StagesTab partner="platform" />
            </TabsContent>
            <TabsContent value="keys" className="px-0">
              <CredentialsTab partner="platform" />
            </TabsContent>
            <TabsContent value="audit" className="px-0">
              <AuditTab partner="platform" />
            </TabsContent>
          </Tabs>
        </PageScroll>
      </Page>
    );
  }

  const p = partner.data;
  return (
    <Page>
      <PageHeader
        eyebrow={back}
        title={
          <span className="inline-flex items-center gap-3">
            {p?.name ?? "…"} {p && <StateBadge state={p.status} title={p.status_reason ?? undefined} />}
          </span>
        }
        description={
          p
            ? `${p.slug} · publishes to ${p.org_slug ? `the ${p.org_slug} library at ` : ""}${p.ingest_url}` +
              (p.deployment === "self_hosted" ? " (self-hosted)" : "")
            : undefined
        }
      />
      <PageScroll>
        {partner.error ? (
          <p className="text-sm text-danger-text">{(partner.error as Error).message}</p>
        ) : (
          p && (
            <Tabs defaultValue="overview">
              <TabsList>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="sources">Sources</TabsTrigger>
                <TabsTrigger value="stages">Models &amp; prompts</TabsTrigger>
                <TabsTrigger value="keys">Keys</TabsTrigger>
                <TabsTrigger value="activity">Documents &amp; runs</TabsTrigger>
                <TabsTrigger value="usage">Spend</TabsTrigger>
                <TabsTrigger value="audit">Changes</TabsTrigger>
              </TabsList>
              <TabsContent value="overview" className="px-0">
                <PartnerOverview partner={p} />
              </TabsContent>
              <TabsContent value="sources" className="px-0">
                <SourcesTab partner={p} />
              </TabsContent>
              <TabsContent value="stages" className="px-0">
                <StagesTab partner={p.id} />
              </TabsContent>
              <TabsContent value="keys" className="px-0">
                <CredentialsTab partner={p.id} ingestCredentialId={p.ingest_credential_id} />
              </TabsContent>
              <TabsContent value="activity" className="px-0">
                <ActivityTab partner={p.id} />
              </TabsContent>
              <TabsContent value="usage" className="px-0">
                <UsageTab partner={p.id} />
              </TabsContent>
              <TabsContent value="audit" className="px-0">
                <AuditTab partner={p.id} />
              </TabsContent>
            </Tabs>
          )
        )}
      </PageScroll>
    </Page>
  );
}
